import React, { useMemo } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { BarChart3 } from "lucide-react";
import { useTranslation } from "react-i18next";

export const MAGNITUDE_RANGES = [
  { label: "<5g", min: 0, max: 5 },
  { label: "5-10g", min: 5, max: 10 },
  { label: "10-15g", min: 10, max: 15 },
  { label: "15-20g", min: 15, max: 20 },
  { label: "20-25g", min: 20, max: 25 },
  { label: "25-30g", min: 25, max: 30 },
  { label: "30g+", min: 30, max: Infinity },
];

// Compact hero tile used in the Headers summary strip. `emphasis` renders the
// total-headers tile in brand orange so it reads as the headline insight.
const HeroStat = ({ label, value, unit, emphasis = false }) => (
  <div
    className={`rounded-xl border p-4 ${
      emphasis ? "border-[#F6D9C4] bg-[#FEF7F3]" : "border-[#F3F4F6] bg-white"
    }`}
  >
    <span className="text-xs font-medium text-[#6B7280]">{label}</span>
    <div className="mt-1.5 flex items-baseline gap-1">
      <span
        className={`text-[28px] font-bold leading-none ${
          emphasis ? "text-[#E07A3A]" : "text-[#111827]"
        }`}
      >
        {value}
      </span>
      {unit && (
        <span className="text-sm font-medium text-[#9CA3AF]">{unit}</span>
      )}
    </div>
  </div>
);

// Defined at module scope to avoid Recharts tooltip re-mount on renders
const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="min-w-[140px] rounded-lg border border-[#F3F4F6] bg-white px-3 py-2.5 shadow-lg">
      <p className="mb-1.5 text-[13px] font-semibold text-[#111827]">{label}</p>
      <div className="space-y-1.5 border-t border-[#F3F4F6] pt-1.5">
        {payload.map((entry) => (
          <div
            key={entry.dataKey}
            className="flex items-center justify-between gap-3"
          >
            <div className="flex items-center gap-1.5">
              <span
                className="h-2 w-2 rounded"
                style={{ backgroundColor: entry.color }}
              />
              <span className="text-[11px] text-[#6B7280]">{entry.name}</span>
            </div>
            <span className="text-xs font-semibold text-[#111827]">
              {entry.value}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

const HeaderChartSection = ({
  title,
  subtitle,
  data,
  hasSelected,
  selectedLabel,
}) => {
  const { t } = useTranslation();
  const hasAnyData = data.some((d) => d.allCount > 0);

  return (
    <div>
      <div className="mb-2 flex items-center justify-between">
        <div>
          <p className="text-xs font-semibold text-[#374151]">{title}</p>
          {subtitle && <p className="text-[11px] text-[#9CA3AF]">{subtitle}</p>}
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1.5">
            <span className="h-2 w-2 rounded bg-[#E07A3A]" />
            <span className="text-[11px] text-[#6B7280]">
              {t("session.headers.allAthletes")}
            </span>
          </div>
          {hasSelected && selectedLabel && (
            <div className="flex items-center gap-1.5">
              <span className="h-2 w-2 rounded bg-[#4F46E5]" />
              <span className="text-[11px] text-[#6B7280]">
                {selectedLabel}
              </span>
            </div>
          )}
        </div>
      </div>

      {!hasAnyData ? (
        <div className="flex h-[180px] flex-col items-center justify-center">
          <p className="text-xs text-[#9CA3AF]">
            {t("session.headers.noData")}
          </p>
        </div>
      ) : (
        <div className="h-[180px]">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={data}
              margin={{ top: 5, right: 5, left: -20, bottom: 5 }}
              barCategoryGap="20%"
            >
              <CartesianGrid
                strokeDasharray="3 3"
                vertical={false}
                stroke="#F3F4F6"
              />
              <XAxis
                dataKey="label"
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 10, fill: "#9CA3AF" }}
                dy={8}
              />
              <YAxis
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 10, fill: "#9CA3AF" }}
                allowDecimals={false}
                dx={-5}
              />
              <Tooltip
                content={<ChartTooltip />}
                cursor={{ fill: "#F9FAFB" }}
              />
              <Bar
                dataKey="allCount"
                name={t("session.headers.allAthletes")}
                fill="#E07A3A"
                radius={[3, 3, 0, 0]}
                maxBarSize={40}
              />
              {hasSelected && selectedLabel && (
                <Bar
                  dataKey="selectedCount"
                  name={selectedLabel}
                  fill="#4F46E5"
                  radius={[3, 3, 0, 0]}
                  maxBarSize={40}
                />
              )}
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
};

const HeaderAnalysis = ({
  playerHeaderStats,
  headerImpacts,
  headerTimelineBuckets,
  headerMagnitudeBuckets,
  headerThreshold,
  sessionStartMs,
  // Selection is driven by the parent (the session's athlete list); picking a
  // player there overlays their headers vs. the team in the charts below.
  selectedPlayerId = null,
}) => {
  const { t } = useTranslation();
  const selectedPlayer = playerHeaderStats.find(
    (p) => p.id === selectedPlayerId
  );

  const timelineBucketsWithSelected = useMemo(() => {
    if (!selectedPlayerId || !sessionStartMs) return headerTimelineBuckets;
    const BUCKET_MS = 5 * 60000;
    return headerTimelineBuckets.map((bucket) => {
      const minuteOffset = parseInt(bucket.label, 10);
      const bucketStart = sessionStartMs + minuteOffset * 60000;
      const bucketEnd = bucketStart + BUCKET_MS;
      const selectedCount = headerImpacts.filter((i) => {
        if (i.playerId !== selectedPlayerId) return false;
        const ts = new Date(i.timestamp).getTime();
        return ts >= bucketStart && ts < bucketEnd;
      }).length;
      return { ...bucket, selectedCount };
    });
  }, [selectedPlayerId, sessionStartMs, headerTimelineBuckets, headerImpacts]);

  const magnitudeBucketsWithSelected = useMemo(() => {
    if (!selectedPlayerId) return headerMagnitudeBuckets;
    return MAGNITUDE_RANGES.map((range, i) => ({
      label: headerMagnitudeBuckets[i]?.label ?? range.label,
      allCount: headerMagnitudeBuckets[i]?.allCount ?? 0,
      selectedCount: headerImpacts.filter((impact) => {
        if (impact.playerId !== selectedPlayerId) return false;
        const g = Number(impact.linear_g) || 0;
        return g >= range.min && g < range.max;
      }).length,
    }));
  }, [selectedPlayerId, headerMagnitudeBuckets, headerImpacts]);

  const playersWithHeaders = playerHeaderStats.filter((p) => p.headerCount > 0);

  // Headline numbers for the summary strip
  const totalHeaders = headerImpacts.length;
  const athletesInvolved = playersWithHeaders.length;
  const peakHeaderG = useMemo(
    () =>
      headerImpacts.length
        ? Math.max(...headerImpacts.map((i) => Number(i.linear_g) || 0))
        : 0,
    [headerImpacts]
  );
  const clusterCount = playerHeaderStats.filter((p) => p.hasCluster).length;

  if (headerImpacts.length === 0) {
    return (
      <div className="flex h-[280px] flex-col items-center justify-center text-center">
        <div className="mb-3 flex h-14 w-14 items-center justify-center rounded-xl bg-[#FEF3E7]">
          <BarChart3 size={28} className="text-[#E07A3A]" />
        </div>
        <h4 className="mb-1 text-sm font-semibold text-[#111827]">
          {t("session.headers.emptyTitle")}
        </h4>
        <p className="max-w-[240px] text-xs leading-relaxed text-[#6B7280]">
          {t("session.headers.emptyBody", { threshold: headerThreshold })}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Headline summary strip — total headers is the main insight */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <HeroStat
          label={t("session.headers.totalHeaders")}
          value={totalHeaders}
          emphasis
        />
        <HeroStat
          label={t("session.headers.athletes")}
          value={athletesInvolved}
        />
        <HeroStat
          label={t("session.headers.peakHeader")}
          value={peakHeaderG.toFixed(1)}
          unit="g"
        />
        <HeroStat
          label={t("session.headers.headerClusters")}
          value={clusterCount}
        />
      </div>

      {/* Timeline Chart — headers tracked over the course of the session */}
      <HeaderChartSection
        title={t("session.headers.overTimeTitle")}
        subtitle={t("session.headers.overTimeSubtitle", {
          threshold: headerThreshold,
        })}
        data={timelineBucketsWithSelected}
        hasSelected={!!selectedPlayerId}
        selectedLabel={selectedPlayer?.name ?? null}
      />

      {/* Magnitude Chart */}
      <HeaderChartSection
        title={t("session.headers.magnitudeTitle")}
        subtitle={t("session.headers.magnitudeSubtitle")}
        data={magnitudeBucketsWithSelected}
        hasSelected={!!selectedPlayerId}
        selectedLabel={selectedPlayer?.name ?? null}
      />
    </div>
  );
};

export default HeaderAnalysis;
